import { nodes } from "./nodes.js";
import { evaluateLinkedExpression } from "./evaluation.js";
import { coreEngine } from "./engine.js";

// Single source of truth for linked property subscription state
const linkedPropertiesStore = {};
const linkedSubscribersStore = {};

function isSubscriberProperty(uid, propertyName) {
  return Boolean(linkedSubscribersStore[uid]?.propertyNames.includes(propertyName));
}

function getPropertySubscribers(uid, property) {
  return linkedPropertiesStore[uid]?.[property] ?? null;
}

let _propagationDepth = 0;

function propagate(publisherUid, propertyName) {
  const subscribers = linkedPropertiesStore[publisherUid]?.[propertyName];
  if (!subscribers) return;

  if (_propagationDepth >= 8) return;
  _propagationDepth++;

  for (const [subscriberUid, properties] of Object.entries(subscribers)) {
    for (const [subscriberProp, expression] of Object.entries(properties)) {
      const { value: resolvedValue } = evaluateLinkedExpression(expression);
      coreEngine.update({ uid: subscriberUid, key: subscriberProp, value: resolvedValue });
    }
  }

  _propagationDepth--;
}

function checkPendingSubscribers(uid) {
  const publisherEntries = linkedPropertiesStore[uid];
  if (!publisherEntries) return;

  const node = nodes.get(uid);
  if (!node) return;

  node._publishedProperties ??= new Set();

  for (const propertyName of Object.keys(publisherEntries)) {
    node._publishedProperties.add(propertyName);
    propagate(uid, propertyName);
  }
}

function unsubscribeNode(uid) {
  const linkedTargets = linkedSubscribersStore[uid] ?? {
    publisherUids: {},
  };

  for (const target of Object.keys(linkedTargets.publisherUids)) {
    for (const property of linkedTargets.publisherUids[target]) {
      if (linkedPropertiesStore[target] && linkedPropertiesStore[target][property]) {
        delete linkedPropertiesStore[target][property][uid];
      }

      // Clean up _publishedProperties on the publisher node
      const publisherNode = nodes.get(target);
      if (publisherNode && publisherNode._publishedProperties) {
        const remainingSubscribers = linkedPropertiesStore[target]?.[property];
        if (!remainingSubscribers || Object.keys(remainingSubscribers).length === 0) {
          publisherNode._publishedProperties.delete(property);
        }
        if (publisherNode._publishedProperties.size === 0) {
          delete publisherNode._publishedProperties;
        }
      }
    }

    if (Object.values(linkedPropertiesStore[target] ?? {}).length === 0) {
      delete linkedPropertiesStore[target];
    }
  }

  delete linkedSubscribersStore[uid];
}

function subscribe(publisherPropertyUid, subscriberPropertyUid, expression) {
  const pubParts = publisherPropertyUid.split(".");
  const publisherUid = pubParts[0];
  const publisherProp = pubParts.slice(1).join(".");

  const subParts = subscriberPropertyUid.split(".");
  const subscriberUid = subParts[0];
  const subscriberProp = subParts.slice(1).join(".");

  // Populate linkedPropertiesStore (publisher -> subscriber mapping)
  linkedPropertiesStore[publisherUid] ??= {};
  linkedPropertiesStore[publisherUid][publisherProp] ??= {};
  linkedPropertiesStore[publisherUid][publisherProp][subscriberUid] ??= {};
  linkedPropertiesStore[publisherUid][publisherProp][subscriberUid][subscriberProp] = expression;

  // Mark publisher node's _publishedProperties if it already exists
  const publisherNode = nodes.get(publisherUid);
  if (publisherNode) {
    publisherNode._publishedProperties ??= new Set();
    publisherNode._publishedProperties.add(publisherProp);
  }

  // Populate linkedSubscribersStore (subscriber -> publisher mapping)
  linkedSubscribersStore[subscriberUid] ??= {
    propertyNames: [],
    publisherUids: {},
  };

  linkedSubscribersStore[subscriberUid].propertyNames.push(subscriberProp);

  linkedSubscribersStore[subscriberUid].publisherUids[publisherUid] ??= [];
  linkedSubscribersStore[subscriberUid].publisherUids[publisherUid].push(publisherProp);
}

export { linkedPropertiesStore, linkedSubscribersStore, isSubscriberProperty, getPropertySubscribers, unsubscribeNode, subscribe, propagate, checkPendingSubscribers };
